/**
 * Telemetry Service — Fire-and-forget event collection to Firestore.
 *
 * Events queue in memory and batch-flush periodically or on visibility change.
 * Never throws. Never blocks rendering.
 */

import { type Firestore, writeBatch, doc, collection, Timestamp } from 'firebase/firestore';
import { ENGINE_VERSION } from '@/lib/engine';

// ── Event types ──

export type TelemetryEventType =
  // Input behavior
  | 'input_focused' | 'input_changed' | 'input_cleared'
  // Navigation
  | 'section_entered' | 'section_exited' | 'flow_abandoned' | 'flow_completed' | 'calculator_loaded'
  // Report
  | 'report_generated' | 'report_section_viewed' | 'report_dwelled'
  // Override signals
  | 'value_overridden' | 'tier_changed' | 'price_rejected'
  // Override intent
  | 'override_intent_tagged'
  // Engagement
  | 'share_initiated' | 'session_started' | 'session_ended'
  // Freemium tier
  | 'price_check_started' | 'price_check_saved'
  | 'upgrade_sheet_opened' | 'upgrade_sheet_dismissed' | 'upgrade_sheet_cta_tapped'
  // PWA install
  | 'pwa_install_banner_shown' | 'pwa_install_banner_dismissed';

export interface TelemetryEvent {
  eventType: TelemetryEventType;
  timestamp: number;
  sessionId: string;
  screen: string;
  engineVersion?: string;
  field?: string;
  previousValue?: string;
  newValue?: string;
  durationMs?: number;
  interactionCount?: number;
  completionPercent?: number;
  meta?: Record<string, string | number | boolean>;
}

// ── Module state ──

interface TelemetryConfig {
  db: Firestore;
  userId: string;
  batchSize: number;
  flushIntervalMs: number;
  enabled: boolean;
  engineVersion: string;
  sessionId: string;
}

let config: TelemetryConfig | null = null;
let queue: TelemetryEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

// ── Public API ──

export function initTelemetry(opts: {
  db: Firestore;
  userId: string;
  batchSize?: number;
  flushIntervalMs?: number;
  enabled?: boolean;
  engineVersion?: string;
}) {
  const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  config = {
    db: opts.db,
    userId: opts.userId,
    batchSize: opts.batchSize ?? 10,
    flushIntervalMs: opts.flushIntervalMs ?? 30000,
    enabled: opts.enabled ?? true,
    engineVersion: opts.engineVersion ?? ENGINE_VERSION,
    sessionId,
  };

  // Periodic flush
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = setInterval(flushQueue, config.flushIntervalMs);

  // Flush on tab hide
  document.addEventListener('visibilitychange', handleVisibility);

  // Session start event
  trackEvent({ eventType: 'session_started', screen: 'app', timestamp: 0, sessionId: '' });
}

export function trackEvent(event: Partial<TelemetryEvent> & { eventType: TelemetryEventType; screen: string }) {
  if (!config?.enabled) return;

  const fullEvent: TelemetryEvent = {
    timestamp: Date.now(),
    sessionId: config.sessionId,
    engineVersion: config.engineVersion,
    ...event,
  };

  queue.push(fullEvent);

  if (queue.length >= config.batchSize) {
    flushQueue();
  }
}

export async function flushQueue() {
  if (!config || queue.length === 0) return;

  const batch = [...queue];
  queue = [];

  try {
    const wb = writeBatch(config.db);
    const colRef = collection(config.db, 'users', config.userId, 'telemetry');

    for (const event of batch) {
      const docRef = doc(colRef);
      wb.set(docRef, {
        ...event,
        timestamp: Timestamp.fromMillis(event.timestamp),
        _writtenAt: Timestamp.now(),
      });
    }

    await wb.commit();
  } catch (err) {
    // Re-queue on failure
    queue = [...batch, ...queue];
    console.warn('[Telemetry] Flush failed, re-queued', err);
  }
}

export function disableTelemetry() {
  if (config) config.enabled = false;
  if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
  queue = [];
  document.removeEventListener('visibilitychange', handleVisibility);
}

// ── Internal ──

function handleVisibility() {
  if (document.visibilityState === 'hidden') {
    flushQueue();
  }
}
